/**
 * AI Space 专用请求工具
 *
 * 用法：
 *   import { get, post } from '../utils/request'
 *
 *   // GET 请求
 *   const data = await get('/web/xxx/list', { groupId: 'abc', page: 1 })
 *
 *   // POST 请求
 *   const data = await post('/web/xxx/create', { name: '测试', groupId: 'abc' })
 *
 * - 自动携带登录 Cookie（credentials: 'include'）
 * - 401 时自动跳转登录页
 * - 接口报错时 Promise.reject，可用 try/catch 捕获
 */

// __API_SERVER_HOST__ 是 Webpack 注入的全局变量，会自动替换为当前环境的 API 地址
declare const __API_SERVER_HOST__: string

type Params = Record<string, string | number | boolean | null | undefined>
type Body = Record<string, unknown>

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  message?: string
  msg?: string
}

function buildUrl(path: string, params?: Params): string {
  const base = __API_SERVER_HOST__ + path
  if (!params) return base

  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
    )
    .join('&')

  return query ? `${base}?${query}` : base
}

async function request<T = unknown>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  params?: Params,
  body?: Body
): Promise<T> {
  const url = buildUrl(path, params)

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined
  })

  // HTTP 层面的 401（未登录）
  if (response.status === 401) {
    window.location.href = '/'
    return Promise.reject(new Error('未登录'))
  }

  const result: ApiResponse<T> & { status?: number } = await response.json()

  // 兼容两种响应结构：
  //  1) envelope: { code: 200, msg, data: {...} }
  //  2) 直接业务体: { status: 0, ... }（如 /web/user/photo/* 系列）
  if (result && typeof result.code === 'number') {
    if (result.code === 401) {
      window.location.href = '/'
      return Promise.reject(new Error('登录状态失效，请重新登录'))
    }
    if (result.code !== 200) {
      return Promise.reject(
        result.message || result.msg || `请求失败 (code: ${result.code})`
      )
    }
    return result.data
  }

  return result as unknown as T
}

/** GET 请求，params 为 URL 查询参数 */
export function get<T = unknown>(path: string, params?: Params): Promise<T> {
  return request<T>(path, 'GET', params)
}

/** POST 请求，body 为 JSON 请求体 */
export function post<T = unknown>(path: string, body?: Body): Promise<T> {
  return request<T>(path, 'POST', undefined, body)
}
